import React, { useState } from "react";
import { Box, Typography, Button, Chip } from "@mui/material";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { IMedia } from "../../../Api/Interface/api.interface";
import { NAVY, BORDER, TEXT_PRIMARY, TEXT_SECONDARY, WARNING } from "../../../theme/tokens";
import MediaPickerDialog from "./MediaPickerDialog";
import { formatBytes, formatDuration } from "./mediaUtils";

/**
 * User-friendly media selector for Image / Video zones. Replaces the raw
 * numeric "Media ID" field: the operator picks or uploads a file and the
 * internal media_id is stored for them. The resolved media record (passed
 * in from the page-level library query) drives the preview + metadata, so
 * old templates that only have a media_id still display correctly.
 */

interface MediaFieldProps {
  kind: "image" | "video";
  mediaId: number | null;
  /** Resolved media record for mediaId, if found in the company library. */
  media?: IMedia;
  companyId?: number;
  onPick: (media: IMedia) => void;
  onRemove: () => void;
}

const MediaField: React.FC<MediaFieldProps> = ({
  kind, mediaId, media, companyId, onPick, onRemove,
}) => {
  const [picker, setPicker] = useState<null | { upload: boolean }>(null);
  const label = kind === "video" ? "Video" : "Image";

  const openBrowse = () => setPicker({ upload: false });
  const openUpload = () => setPicker({ upload: true });

  const handleSelect = (m: IMedia) => {
    setPicker(null);
    onPick(m);
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mb: 0.75 }}>
        {label} Source
      </Typography>

      {mediaId == null ? (
        // No media yet — required indicator + the two entry points.
        <Box sx={{
          border: `1px dashed ${WARNING}`, borderRadius: 1.5, p: 1.25,
          bgcolor: "rgba(234,88,12,0.05)",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
            <ErrorOutlineIcon sx={{ fontSize: 16, color: WARNING }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: WARNING }}>
              {label} required
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" variant="outlined" fullWidth
              startIcon={<PhotoLibraryIcon sx={{ fontSize: 16 }} />}
              onClick={openBrowse}
              sx={{ textTransform: "none", fontWeight: 700, borderColor: BORDER, color: NAVY }}>
              Library
            </Button>
            <Button size="small" variant="outlined" fullWidth
              startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
              onClick={openUpload}
              sx={{ textTransform: "none", fontWeight: 700, borderColor: BORDER, color: NAVY }}>
              Upload
            </Button>
          </Box>
        </Box>
      ) : (
        // Selected — preview card with metadata + Replace / Remove.
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: "hidden" }}>
          <Box sx={{ position: "relative", pt: "52%", bgcolor: "#0b1e2d" }}>
            {media ? (
              media.media_type === "video" ? (
                <Box component="video" src={media.url} muted preload="metadata"
                  sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
              ) : (
                <Box component="img" src={media.url} alt={media.original_name}
                  sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
              )
            ) : (
              <Box sx={{
                position: "absolute", inset: 0, display: "flex",
                alignItems: "center", justifyContent: "center", textAlign: "center", px: 1,
              }}>
                <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
                  Media #{mediaId}
                  <br />
                  (not found in library)
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ p: 1 }}>
            <Typography noWrap title={media?.original_name}
              sx={{ fontSize: 12, fontWeight: 700, color: TEXT_PRIMARY }}>
              {media?.original_name ?? `Media #${mediaId}`}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", mt: 0.25, flexWrap: "wrap" }}>
              {formatDuration(media?.duration) && (
                <Chip size="small" label={formatDuration(media?.duration)}
                  sx={{ height: 18, fontSize: 10 }} />
              )}
              <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                {formatBytes(media?.file_size)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button size="small" variant="outlined" fullWidth
                startIcon={<SwapHorizIcon sx={{ fontSize: 16 }} />}
                onClick={openBrowse}
                sx={{ textTransform: "none", fontWeight: 700, borderColor: BORDER, color: NAVY }}>
                Replace
              </Button>
              <Button size="small" variant="outlined" fullWidth color="error"
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                onClick={onRemove}
                sx={{ textTransform: "none", fontWeight: 700 }}>
                Remove
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {picker && (
        <MediaPickerDialog
          open
          kind={kind}
          companyId={companyId}
          initialUpload={picker.upload}
          onClose={() => setPicker(null)}
          onSelect={handleSelect}
        />
      )}
    </Box>
  );
};

export default MediaField;
